/**
 * IconFilePickerDialog — select an image file from Fias storage to use as
 * an arche icon. Unlike the AI-context FilePickerDialog, this returns the
 * raw File blob so the caller can run it through the admin icon upload
 * pipeline (presigned PUT + confirm → variant generation).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { ImageIcon, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { FileBrowser } from '@/components/files';
import { api, type UserFileResponse } from '@/lib/api';

const ALLOWED_ICON_MIME_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'];
const ALLOWED_ICON_EXTENSIONS = ['png', 'jpg', 'jpeg', 'webp', 'svg'];

export function isIconFile(file: UserFileResponse): boolean {
  if (file.mimeType && ALLOWED_ICON_MIME_TYPES.includes(file.mimeType)) return true;
  // Fall back to extension for files uploaded without a recorded MIME type.
  const ext = file.fileExtension?.toLowerCase() ?? '';
  return ALLOWED_ICON_EXTENSIONS.includes(ext);
}

export interface IconFilePickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with a File blob ready to run through the icon upload flow. */
  onFileSelected: (file: File) => void;
}

export function IconFilePickerDialog({
  open,
  onOpenChange,
  onFileSelected,
}: IconFilePickerDialogProps) {
  const [selected, setSelected] = useState<UserFileResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Bump a counter each time the dialog opens so FileBrowser refetches fresh
  // data. Works around `useFiles` using local state that goes stale across
  // remounts when React Query's 30s staleTime suppresses the mount refetch.
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setRefreshTrigger((n) => n + 1);
    }
    prevOpenRef.current = open;
  }, [open]);

  const handleSelect = useCallback((file: UserFileResponse) => {
    if (!isIconFile(file)) {
      setError(
        `"${file.fileName}" isn't a supported image type. Choose a PNG, JPEG, WebP, or SVG file.`,
      );
      setSelected(null);
      return;
    }
    setError(null);
    setSelected(file);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!selected) return;
    setLoading(true);
    setError(null);
    try {
      const { downloadUrl } = await api.getDownloadUrl(selected.fileId);
      const response = await fetch(downloadUrl);
      if (!response.ok) {
        throw new Error(`Download failed (${response.status})`);
      }
      const blob = await response.blob();
      const mimeType = selected.mimeType || blob.type || 'application/octet-stream';
      const file = new File([blob], selected.fileName, { type: mimeType });
      onFileSelected(file);
      setSelected(null);
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load file');
    } finally {
      setLoading(false);
    }
  }, [selected, onFileSelected, onOpenChange]);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (loading) return;
      if (!next) {
        setSelected(null);
        setError(null);
      }
      onOpenChange(next);
    },
    [loading, onOpenChange],
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-4xl h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ImageIcon className="h-5 w-5" />
            Choose Icon from Fias Files
          </DialogTitle>
          <DialogDescription>
            Pick a PNG, JPEG, WebP, or SVG file from your Fias storage. The icon must be 1024×1024
            for raster formats. Sized variants (64/128/256/512 px) are generated automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-hidden border rounded-lg">
          <FileBrowser
            onFileSelect={handleSelect}
            multiSelect={false}
            status="active"
            className="h-full"
            refreshTrigger={refreshTrigger}
          />
        </div>

        {error && (
          <div className="text-sm px-3 py-2 rounded-md bg-destructive/10 text-destructive">
            {error}
          </div>
        )}

        {selected && !error && (
          <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-2 min-w-0">
              <ImageIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <span className="truncate font-medium">{selected.fileName}</span>
            </div>
            <Button onClick={handleConfirm} disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Loading…
                </>
              ) : (
                'Use as Icon'
              )}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default IconFilePickerDialog;
